import React, { useState, useRef, useContext, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Bounds, AdaptiveDpr, AdaptiveEvents } from '@react-three/drei';
import SimpleReactValidator from 'simple-react-validator';
import { useLocation } from "react-router-dom";
import { useHistory } from 'react-router'
import NotificationSystem from "react-notification-system";
import Cookies from 'universal-cookie';
import { Modal } from "react-bootstrap";
import Floor from '../components/WMS/Floor';
import CameraSetup from '../components/WMS/CameraSetup';
import { FeatureBlueprintContext } from '../context/FeatureBlueprintContext';
import { GlobalLoadIndicator } from '../context/GlobalLoadIndicator';
import { loopBackDomain, loopBackPort } from "../environment/environment";
import { PostAPI } from 'api/post';
import WmsFilters from '../components/WMS/WmsFilters';
import { FetchAPI } from 'api/fetch';
import { vibrantUniqueColors } from 'utilities/CommonUtils';
import "../assets/sass/components/_wms.scss";
import swal from 'sweetalert';

const Scene = () => {
    const controlsRef = useRef(null);
    const notificationRef = useRef(null);
    const location = useLocation();
    const history = useHistory()
    const [featureBlueprint] = useContext(FeatureBlueprintContext);
    const cookies = new Cookies();
    const [is3DView, setIs3DView] = useState(true);
    const [selectedBin, setSelectedBin] = useState(null);
    const [selectedRack, setSelectedRack] = useState(false);
    const [pageLayoutData, setPageLayoutData] = useState('undefined');
    const [pageLayoutDataFromICA, setPageLayoutDataFromICA] = useState(false);
    const [wmsContext, setWmsContext] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [showParentInfo, setShowParentInfo] = useState(false);
    const [warehouseData, setWarehouseData] = useState(null);
    const [openForm, setOpenForm] = useState(true);
    const [legendData, setLegendData] = useState([])
    const [showModal, setShowModal] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState({
        entity: null,
        color: vibrantUniqueColors
    });
    const [binCounts, setBinCounts] = useState({
        totalBins: null,
        emptyBins: null,
        materialCount: null
    })
    const [binData, setBinData] = useState({ columns: [], rowData: [] })
    const [isLoading, setIsLoading] = useState(false);
    const [showAsideFilters, setShowAsideFilters] = useState(true);
    const clientToken = cookies.get("clientToken");
    const cookie_access_token = cookies.get("accessToken");
    const [globalLoadIndicator, setGlobalLoadIndicator] = useContext(GlobalLoadIndicator);
    const pageLayoutDataContext = (featureBlueprint.data == undefined) ? 'undefined' : featureBlueprint.data.featureBlueprintymlData.pageTypes[0].bodyLayout;
    const allowedICAOrigins = ["https://icapidilite-stg.eseal.io"];
    const fallbackTimeoutRef = useRef(null);

    const validator = new SimpleReactValidator({
        autoForceUpdate: this,
        element: (message, className) => <div className="text-danger">{message}</div>
    });

    const dynamicParameters = {
        customer_id: cookies.get("customer_id"),
        user_id: cookies.get("user_id"),
        userLocation_id: cookies.get("userLocation_id"),
        userRole_name: cookies.get("userRole_name"),
    }

    // Handshake-based communication with ICA
    useEffect(() => {
        const handleMessage = (event) => {
            // Only accept messages from allowed ICA origins
            if (!allowedICAOrigins.includes(event.origin)) {
                return;
            }
            
            // Handle WMS_INITIAL_DATA message from ICA
            if (event.data?.type === 'WMS_INITIAL_DATA' && event.data?.pageLayoutData) {
                setPageLayoutData(event.data.pageLayoutData);
                setWmsContext(event.data.wmsContext || null);
                setPageLayoutDataFromICA(true);
                // Clear fallback timeout since we received data from ICA
                if (fallbackTimeoutRef.current) {
                    clearTimeout(fallbackTimeoutRef.current);
                }
            }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Send WMS_READY signal to parent (ICA) to initiate handshake
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'WMS_READY' }, '*');
        }
        
        return () => {
            window.removeEventListener('message', handleMessage);
            if (fallbackTimeoutRef.current) {
                clearTimeout(fallbackTimeoutRef.current);
            }
        };
    }, [location, cookie_access_token, pageLayoutDataFromICA]);

    const getFeatureData = async () => {
        const featureID = 1;
        const pageTypeID = 6;
        const payload = {
            pageTypeID,
            featureID
        };
        const url = loopBackDomain + ':' + loopBackPort + '/api/v1/rendering-engines/getFeatureBlueprint';
        const response = await PostAPI(url, payload)
        if (response.status === 200) {
            setPageLayoutData(response?.data?.featureBlueprintymlData?.pageTypes[0].bodyLayout)
        } else {
            setPageLayoutData([])
        }
    }
    const showNotification = (type, message) => {
        if (notificationRef.current) {
            notificationRef.current.addNotification({
                message,
                level: type,
                position: "tr",
                autoDismiss: 3,
            });
        }
    };

    const userAuthentication = async () => {
        const queryParams = new URLSearchParams(location.search);
        const accessToken = queryParams.get('access_token');
        if (accessToken) {
            if ((cookie_access_token === 'undefined') || (cookie_access_token !== accessToken)) {
                const endpoint = `https://product-stg.eseal.io:5004/product/v1/verify-access-token/?access_token=${accessToken}`
                const response = await FetchAPI(endpoint)
                const responseJson = await response.json()
                if (responseJson?.status === 1) {
                    showNotification("success", responseJson.message);
                    cookies.set('accessToken', accessToken, { path: '/' });
                    cookies.set('customer_id', responseJson.customer_id, { path: '/' });
                    cookies.set('user_id', responseJson.user_id, { path: '/' });
                    cookies.set('userLocation_id', responseJson.user_location_id, { path: '/' });
                    localStorage.setItem("wms", JSON.stringify(responseJson.wms));
                    getFeatureData()
                } else {
                    swal({ icon: 'error', title: "Error", text: responseJson?.message ?? "Something went wrong" })
                }
            } else {
                getFeatureData();
            }
            const url = new URL(window.location.href);
            if (url.searchParams.has('access_token')) {
                url.searchParams.delete('access_token');
                window.history.replaceState({}, document.title, url.pathname + url.search);
            }
        } else {
            if (cookie_access_token) {
                getFeatureData();
            } else {
                swal({ icon: "error", title: "Error", text: "Access token verification failed" })
            }
        }
    }

    const handleOpenBinDetails = async () => {
        setIsLoading(true)
        const payload = {
            ...dynamicParameters,
            bin_code: selectedBin.entity_name,
            userLocation_id: cookies.get("userLocation_id"),
        }
        let endpoint = pageLayoutData[1].subComponentsItems.binInfoEndpoint;
        const paramcomString = new URLSearchParams(payload);
        endpoint = (`${endpoint}?${paramcomString.toString()}`);
        const response = await FetchAPI(endpoint)
        const responseJson = await response.json()
        if (responseJson?.data?.data?.length) {
            const columns = Object.keys(responseJson?.data?.data[0])
            const rowData = responseJson?.data?.data
            setBinData({ columns, rowData })
        }else setBinData({ columns: [], rowData: [] })
        setIsLoading(false)
        setShowModal(true)
    }

    useEffect(() => {
        // Detect iframe mode once
        const queryParams = new URLSearchParams(location.search);
        const isIframeMode = queryParams.get('iframe') === 'true';
        
        // Skip globalLoadIndicator synchronization in iframe mode
        // In iframe mode, pageLayoutData is loaded via the ICA WMS_INITIAL_DATA handshake and must not be overwritten
        if (!isIframeMode) {
            if (globalLoadIndicator == '2') {
                setPageLayoutData(pageLayoutDataContext);
            } else if (globalLoadIndicator == '1') {
                setPageLayoutData('undefined');
            }
        }
    }, [globalLoadIndicator, location]);

    useEffect(() => {
        if (location.pathname === "/3d-wms") {
            const queryParams = new URLSearchParams(location.search);
            const isIframeMode = queryParams.get('iframe') === 'true';
            
            if (isIframeMode) {
                // Skip authentication and feature-blueprint fallback entirely when loaded inside iframe.
                // ICA is the sole source of pageLayoutData/wmsContext via the WMS_INITIAL_DATA handshake;
                // Product must not require OAuth/login or call getFeatureData() in this mode.
            } else {
                // Normal Product user authentication flow
                if (clientToken) {
                    userAuthentication()
                }
            }
        }
    }, [location, clientToken, cookie_access_token, pageLayoutDataFromICA])
    return (
        <div className='container-3d-main'>
            <NotificationSystem ref={notificationRef} />
            {pageLayoutData != undefined && (
                <WmsFilters
                    warehouseData={warehouseData}
                    pageLayoutData={pageLayoutData}
                    setWarehouseData={setWarehouseData}
                    setSelectedEntity={setSelectedEntity}
                    selectedEntity={selectedEntity}
                    binCounts={binCounts}
                    setBinCounts={setBinCounts}
                    showAsideFilters={showAsideFilters}
                    wmsContext={wmsContext}
                />)
            }
            {warehouseData && (
                <div className="canvas-container"
                style={showAsideFilters ? {width: "75%"} : {width: "100%"}}
                // style={(window.innerWidth <= 768 && (!openForm)) ? { height: '75vh' } : {}}
                >
                    <Canvas
                        orthographic={!is3DView}
                        camera={{ position: [230, 230, 230], fov: 45 }}
                        dpr={[0.75, 1.5]}
                        shadows={false}
                        flat
                        gl={{ 
                            powerPreference: 'high-performance', 
                            antialias: false, 
                            stencil: false, 
                            depth: true
                        }}
                        frameloop="always"
                        performance={{ min: 0.5 }}
                    >
                        <color attach="background" args={['#f5f5f5']} />
                        <fog attach="fog" args={['#f5f5f5', 400, 1000]} />
                        <ambientLight intensity={0.6} />
                        <directionalLight intensity={0.6} position={[200, 300, 200]} castShadow={false} />

                        {warehouseData.en_name === "Floor" && (
                            <Floor
                                floor={warehouseData}
                                onBinClick={setSelectedBin}
                                selectedBin={selectedBin}
                                selectedRack={selectedRack}
                                setSelectedRack={setSelectedRack}
                                setLegendData={setLegendData}
                                selectedEntity={selectedEntity}
                                setBinCounts={setBinCounts}
                            />)
                        }

                        <OrbitControls
                            ref={controlsRef}
                            makeDefault
                            enableRotate={is3DView}
                            enableZoom
                            enablePan
                            enableDamping
                            dampingFactor={0.15}
                            rotateSpeed={1.2}
                            zoomSpeed={1.5}
                            panSpeed={1.2}
                            maxPolarAngle={is3DView ? Math.PI : 0}
                            minPolarAngle={is3DView ? 0 : 0}
                            minDistance={40}
                            maxDistance={800}
                            zoomToCursor
                            screenSpacePanning={false}
                        />
                        {/* Adapt resolution and event processing dynamically during interaction to avoid frame drops */}
                        <AdaptiveDpr pixelated />
                        <AdaptiveEvents />
                        {/* <CameraSetup is3DView={is3DView} /> */}
                        {/* <axesHelper args={[200,200,200]} /> */}
                    </Canvas>

                    {selectedBin && (
                        <div onClick={handleOpenBinDetails} className="aside-button">
                            {isLoading ? "Loading..." : "Show info"}
                        </div>
                    )}
                    <div onClick={() => setShowAsideFilters(!showAsideFilters)} className="aside-button-filters">
                            {`${showAsideFilters ? "Hide" : "Show"} Filters`}
                        </div>
                    {showModal && (
                        <Modal show={true} backdrop="static" keyboard={false} dialogClassName={"modal-70w"} className="modal-custom" onHide={() => setShowModal(false)} >
                            <span className="close-icon-custom" onClick={() => setShowModal(false)}>
                                &times;
                            </span>
                            <Modal.Header key="MHeader" className="modal-header-custom">
                                <Modal.Title key="MTitle">{`Bin: ${selectedBin.entity_name}`}</Modal.Title>
                            </Modal.Header>

                            <Modal.Body className="modal-body">
                                {binData.rowData?.length ? (
                                    <div className="entity-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    {binData.columns.map((column) => (
                                                        <th key={column}>{column.replace(/_/g, ' ').toUpperCase()}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {binData.rowData.map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                        {binData.columns.map((column) => (
                                                            <td key={column}>{row[column]}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) :
                                    <div style={{ margin: "5rem" }} >
                                        <h4>No Data to Display</h4>
                                    </div>}
                            </Modal.Body>
                            <Modal.Footer>
                            </Modal.Footer>
                        </Modal>
                    )}
                </div>
            )}
        </div>
    );
};

export default Scene;
